import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import PwaInstallBanner from './components/PwaInstallBanner';

import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Invite from './pages/Invite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DashboardLayout from './pages/DashboardLayout';
import PublicQrRequest from './pages/PublicQrRequest';
import PublicStorePickup from './pages/PublicStorePickup';
import ReportView from './pages/ReportView';

// Admin
import AdminOverview from './pages/admin/AdminOverview';
import WorkOrders from './pages/admin/WorkOrders';
import DuplicateWorkOrders from './pages/admin/DuplicateWorkOrders';
import Assets from './pages/admin/Assets';
import DocumentLibrary from './pages/admin/DocumentLibrary';
import Parts from './pages/admin/Parts';
import Stores from './pages/admin/Stores';
import StoreDetail from './pages/admin/StoreDetail';
import XeroSettings from './pages/admin/XeroSettings';
import AddressLookupSettings from './pages/admin/AddressLookupSettings';
import Quotes from './pages/admin/Quotes';
import Users from './pages/admin/Users';
import LoginActivity from './pages/admin/LoginActivity';
import EngineerProfile from './pages/admin/EngineerProfile';
import RateCards from './pages/admin/RateCards';
import Timesheets from './pages/admin/Timesheets';
import TimesheetCorrections from './pages/admin/TimesheetCorrections';
import EmployeeDocuments from './pages/admin/EmployeeDocuments';
import Customers from './pages/admin/Customers';
import CustomerDetail from './pages/admin/CustomerDetail';
import CustomerSiteAssets from './pages/admin/CustomerSiteAssets';
import AdminSchedule from './pages/admin/AdminSchedule';
import AdminInvoices from './pages/admin/AdminInvoices';
import PdfTemplates from './pages/admin/PdfTemplates';
import BoardLayouts from './pages/admin/BoardLayouts';
import BoardLayoutEditor from './pages/admin/BoardLayoutEditor';
import Taxonomy from './pages/admin/Taxonomy';
import AdminInvoiceDetail from './pages/admin/AdminInvoiceDetail';
import Bills from './pages/admin/Bills';
import BillDetail from './pages/admin/BillDetail';
import Suppliers from './pages/admin/Suppliers';
import SupplierDetail from './pages/admin/SupplierDetail';
import DraftInventory from './pages/admin/DraftInventory';
import InventorySettings from './pages/admin/InventorySettings';
import NotificationSettings from './pages/admin/NotificationSettings';
import ServiceRequestsAdmin from './pages/admin/ServiceRequestsAdmin';
import QrCodes from './pages/admin/QrCodes';
import CertList from './pages/admin/CertList';
import AdminReports from './pages/admin/AdminReports';
import CertNew from './pages/admin/CertNew';
import CertView from './pages/admin/CertView';
import CertTrain from './pages/admin/CertTrain';
import CertRules from './pages/admin/CertRules';
import ReferenceLibrary from './pages/admin/ReferenceLibrary';
import CertScan from './pages/admin/CertScan';
import EicrReportsList from './pages/admin/EicrReportsList';
import EicrReportEditor from './pages/admin/EicrReportEditor';
import EicrPresetsEditor from './pages/admin/EicrPresetsEditor';
import MarketingArticles from './pages/admin/MarketingArticles';
import MarketingArticleEditor from './pages/admin/MarketingArticleEditor';
import MarketingSchedule from './pages/admin/MarketingSchedule';

// Employee
import EmployeeOverview from './pages/employee/EmployeeOverview';
import TimeClock from './pages/employee/TimeClock';
import MyWorkOrders from './pages/employee/MyWorkOrders';
import MySchedule from './pages/employee/MySchedule';
import Chat from './pages/employee/Chat';
import EmployeeReceiptScan from './pages/employee/EmployeeReceiptScan';

// Lutrom Academy — reusable training course engine
import AcademyCourseManagement from './pages/admin/academy/CourseManagement';
import AcademyCourseAssign from './pages/admin/academy/CourseAssign';
import AcademyCourseEditor from './pages/admin/academy/CourseEditor';
import AcademyTrainingMatrix from './pages/admin/academy/TrainingMatrix';
import AcademyNotifSettings from './pages/admin/academy/NotificationSettings';
import MyTraining from './pages/employee/academy/MyTraining';
import CoursePlayer from './pages/employee/academy/CoursePlayer';
import EmployeeHub from './pages/employee/hub/EmployeeHub';
import MyTrainingHub from './pages/employee/hub/MyTrainingHub';
import MyTimesheetsHub from './pages/employee/hub/MyTimesheetsHub';
import MyDocumentsHub from './pages/employee/hub/MyDocumentsHub';

// Customer
import CustomerOverview from './pages/customer/CustomerOverview';
import CustomerRequests from './pages/customer/CustomerRequests';
import CustomerWorkOrders from './pages/customer/CustomerWorkOrders';
import CustomerSites from './pages/customer/CustomerSites';
import CustomerSchedule from './pages/customer/CustomerSchedule';
import CustomerInvoices from './pages/customer/CustomerInvoices';
import CustomerReports from './pages/customer/CustomerReports';
import CustomerTimesheet from './pages/customer/CustomerTimesheet';
import CustomerQuotes from './pages/customer/CustomerQuotes';
import CustomerSpares from './pages/customer/CustomerSpares';
import CustomerWorkOrder from './pages/customer/CustomerWorkOrder';
import CustomerSuggestions from './pages/customer/CustomerSuggestions';
import AdminSuggestions from './pages/admin/AdminSuggestions';

// Public entry points (/, /login): once a stored session is restored, skip
// straight into the app. iOS Home Screen apps always relaunch at start_url.
const PublicEntry = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center" data-testid="session-restoring">Loading...</div>;
  if (user) {
    const next = new URLSearchParams(location.search).get('next') || '';
    const safe = next.startsWith('/') && !next.startsWith('//') ? next : '/dashboard';
    return <Navigate to={safe} replace />;
  }
  return children;
};

const PrivateRoute = ({ children, allow }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center" data-testid="session-restoring">Loading...</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const RoleRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'employee') return <Navigate to="/employee" replace />;
  if (user.role === 'subcontractor') return <Navigate to="/subcontractor" replace />;
  return <Navigate to="/customer" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <PwaInstallBanner />
        <Routes>
          <Route path="/" element={<PublicEntry><LandingPage /></PublicEntry>} />
          <Route path="/login" element={<PublicEntry><Login /></PublicEntry>} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/dashboard" element={<RoleRedirect />} />

          {/* QR Public/Protected */}
          <Route path="/qr/request/:siteId" element={<PublicQrRequest />} />
          <Route path="/qr/store/:storeId" element={<PublicStorePickup />} />
          <Route path="/report/:reportId" element={<ReportView />} />

          {/* Admin */}
          <Route path="/admin" element={<PrivateRoute allow={['admin']}><DashboardLayout /></PrivateRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="work-orders" element={<WorkOrders />} />
            <Route path="work-orders/duplicates" element={<DuplicateWorkOrders />} />
            <Route path="assets" element={<Assets />} />
            <Route path="procedures" element={<DocumentLibrary />} />
            <Route path="document-library" element={<DocumentLibrary />} />
            <Route path="compliance" element={<DocumentLibrary />} />
            <Route path="parts" element={<Parts />} />
            <Route path="stores" element={<Stores />} />
            <Route path="stores/:storeId" element={<StoreDetail />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="xero" element={<XeroSettings />} />
            <Route path="settings/address-lookup" element={<AddressLookupSettings />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<EngineerProfile />} />
            <Route path="login-activity" element={<LoginActivity />} />
            <Route path="rate-cards" element={<RateCards />} />
            <Route path="timesheets" element={<Timesheets />} />
            <Route path="timesheet-corrections" element={<TimesheetCorrections />} />
            <Route path="employee-documents" element={<EmployeeDocuments />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:customerId" element={<CustomerDetail />} />
            <Route path="customers/:customerId/sites/:siteId" element={<CustomerSiteAssets />} />
            <Route path="schedule" element={<AdminSchedule />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="invoices/:invoiceId" element={<AdminInvoiceDetail />} />
            <Route path="bills" element={<Bills />} />
            <Route path="bills/:billId" element={<BillDetail />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:supplierId" element={<SupplierDetail />} />
            <Route path="inventory/drafts" element={<DraftInventory />} />
            <Route path="inventory/settings" element={<InventorySettings />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="requests" element={<ServiceRequestsAdmin />} />
            <Route path="suggestions" element={<AdminSuggestions />} />
            <Route path="qr-codes" element={<QrCodes />} />
            <Route path="certificates" element={<CertList />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="certificates/new" element={<CertNew />} />
            <Route path="certificates/train" element={<CertTrain />} />
            <Route path="certificates/rules" element={<CertRules />} />
            <Route path="certificates/reference-library" element={<ReferenceLibrary />} />
            <Route path="certificates/scan-previous" element={<CertScan />} />
            <Route path="certificates/:id" element={<CertView />} />
            <Route path="eicr-reports" element={<EicrReportsList />} />
            <Route path="eicr-reports/presets" element={<EicrPresetsEditor />} />
            <Route path="eicr-reports/new" element={<EicrReportEditor />} />
            <Route path="eicr-reports/:id" element={<EicrReportEditor />} />
            <Route path="settings/pdf-templates" element={<PdfTemplates />} />
            <Route path="board-layouts" element={<BoardLayouts />} />
            <Route path="board-layouts/:id" element={<BoardLayoutEditor />} />
            <Route path="settings/lists" element={<Taxonomy />} />
            <Route path="marketing/articles" element={<MarketingArticles />} />
            <Route path="marketing/articles/:articleId" element={<MarketingArticleEditor />} />
            <Route path="marketing/schedule" element={<MarketingSchedule />} />
            {/* Lutrom Academy */}
            <Route path="academy" element={<AcademyCourseManagement />} />
            <Route path="academy/matrix" element={<AcademyTrainingMatrix />} />
            <Route path="academy/notifications" element={<AcademyNotifSettings />} />
            <Route path="academy/:courseId/:version/preview" element={<CoursePlayer preview={true} />} />
            <Route path="academy/:courseId/:version/assign" element={<AcademyCourseAssign />} />
            <Route path="academy/:courseId/:version/edit" element={<AcademyCourseEditor />} />
          </Route>

          {/* Employee */}
          <Route path="/employee" element={<PrivateRoute allow={['employee']}><DashboardLayout /></PrivateRoute>}>
            <Route index element={<EmployeeOverview />} />
            <Route path="time-clock" element={<TimeClock />} />
            <Route path="work-orders" element={<MyWorkOrders />} />
            <Route path="assets" element={<Assets />} />
            <Route path="schedule" element={<MySchedule />} />
            <Route path="chat" element={<Chat />} />
            <Route path="receipt-scan" element={<EmployeeReceiptScan />} />
            <Route path="certificates" element={<CertList />} />
            <Route path="certificates/new" element={<CertNew />} />
            <Route path="certificates/train" element={<CertTrain />} />
            <Route path="certificates/rules" element={<CertRules />} />
            <Route path="certificates/reference-library" element={<ReferenceLibrary />} />
            <Route path="certificates/scan-previous" element={<CertScan />} />
            <Route path="certificates/:id" element={<CertView />} />
            <Route path="eicr-reports" element={<EicrReportsList />} />
            <Route path="eicr-reports/presets" element={<EicrPresetsEditor />} />
            <Route path="eicr-reports/new" element={<EicrReportEditor />} />
            <Route path="eicr-reports/:id" element={<EicrReportEditor />} />
            <Route path="notifications" element={<NotificationSettings />} />
            {/* Employee Hub — personal area */}
            <Route path="hub" element={<EmployeeHub />} />
            <Route path="hub/training" element={<MyTrainingHub />} />
            <Route path="hub/timesheets" element={<MyTimesheetsHub />} />
            <Route path="hub/documents" element={<MyDocumentsHub />} />
            {/* Lutrom Academy — learner side */}
            <Route path="training" element={<MyTraining />} />
            <Route path="training/:courseId/:version" element={<CoursePlayer />} />
          </Route>

          {/* Subcontractor — strictly-scoped employee. Only "My Work Orders". */}
          <Route path="/subcontractor" element={<PrivateRoute allow={['subcontractor']}><DashboardLayout /></PrivateRoute>}>
            <Route index element={<MyWorkOrders />} />
            <Route path="work-orders" element={<MyWorkOrders />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="hub" element={<EmployeeHub />} />
            <Route path="hub/training" element={<MyTrainingHub />} />
            <Route path="hub/timesheets" element={<MyTimesheetsHub />} />
            <Route path="hub/documents" element={<MyDocumentsHub />} />
            <Route path="training" element={<MyTraining />} />
            <Route path="training/:courseId/:version" element={<CoursePlayer />} />
          </Route>

          {/* Customer */}
          <Route path="/customer" element={<PrivateRoute allow={['customer']}><DashboardLayout /></PrivateRoute>}>
            <Route index element={<CustomerOverview />} />
            <Route path="requests" element={<CustomerRequests />} />
            <Route path="work-orders" element={<CustomerWorkOrders />} />
            <Route path="sites" element={<CustomerSites />} />
            <Route path="schedule" element={<CustomerSchedule />} />
            <Route path="assets" element={<Assets />} />
            <Route path="spares" element={<CustomerSpares />} />
            <Route path="suggestions" element={<CustomerSuggestions />} />
            <Route path="quotes" element={<CustomerQuotes />} />
            <Route path="reports" element={<CustomerReports />} />
            <Route path="timesheet" element={<CustomerTimesheet />} />
            <Route path="invoices" element={<CustomerInvoices />} />
            <Route path="work-orders/:id" element={<CustomerWorkOrder />} />
            <Route path="notifications" element={<NotificationSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
